import { Checkbox } from '@/components/UiElements'
import HelpTooltip from '@/components/UiElements/HelpTooltip'
import { settingsConfigHelp } from '@/components/Settings/configHelp'
import { ProfanityRuleItem, ProfanityRuleStatus } from '@/types/profanity'

import DeferredNumberInput from './DeferredNumberInput'
import {
  getActiveProfanityStatuses,
  getEffectiveProfanityThresholds,
  PROFANITY_STATUS_CONFIG,
  ProfanityEditorState,
} from './profanityRuleUtils'

const PROFANITY_STATUS_HELP: Record<ProfanityRuleStatus, string> = {
  [ProfanityRuleStatus.ACCEPTED]: settingsConfigHelp.profanityStatus.accepted,
  [ProfanityRuleStatus.REQUIRES_MANUAL_CHECK]: settingsConfigHelp.profanityStatus.manualCheck,
  [ProfanityRuleStatus.REJECTED]: settingsConfigHelp.profanityStatus.rejected,
}

type ProfanityRuleRowsProps = {
  state: ProfanityEditorState
  rules: ProfanityRuleItem[]
  disabled: boolean
  onActiveChange: (status: ProfanityRuleStatus, active: boolean) => void
  onThresholdChange: (status: ProfanityRuleStatus, value: number) => number
}

export const profanityFieldClassName =
  'h-[44px] rounded-md border border-gray-300 bg-white px-md text-text-md text-gray-900 shadow-xs outline-none ' +
  'transition-all placeholder:text-gray-400 hover:border-primary-500 focus:border-primary-500 focus:shadow-focus disabled:border-gray-200 disabled:bg-gray-50 disabled:text-gray-400'

const ProfanityRuleRows = ({
  state,
  rules,
  disabled,
  onActiveChange,
  onThresholdChange,
}: ProfanityRuleRowsProps) => {
  const activeStatuses = getActiveProfanityStatuses(state)
  const effectiveThresholds = getEffectiveProfanityThresholds(state)

  return (
    <div className="divide-y divide-gray-200">
      {PROFANITY_STATUS_CONFIG.map(({ status, label, barClassName }) => {
        const active = state.activeStatuses[status]
        const activeRuleIndex = rules.findIndex((rule) => rule.rule_status === status)
        const activeRule = activeRuleIndex >= 0 ? rules[activeRuleIndex] : null
        const isLastActiveRule = activeRuleIndex === rules.length - 1
        const threshold = active ? effectiveThresholds[status] : state.thresholds[status]
        const minimum = activeRule ? activeRule.min_threshold + 1 : 0
        const maximum = activeRule && !isLastActiveRule
          ? rules[activeRuleIndex + 1].max_threshold - 1
          : 100
        const checkboxDisabled = disabled || (active && activeStatuses.length === 1)
        const inputDisabled = disabled || !active || isLastActiveRule

        return (
          <HelpTooltip key={status} help={PROFANITY_STATUS_HELP[status]} title={label}>
            <div className="grid grid-cols-12 items-center gap-md py-md first:pt-0 last:pb-0">
              <div className="col-span-9 flex items-center gap-sm">
                <span className={`h-[8px] w-[8px] shrink-0 rounded-full ${barClassName}`} />
                <Checkbox
                  label={label}
                  defaultChecked={active}
                  disabled={checkboxDisabled}
                  onChange={(checked) => onActiveChange(status, checked)}
                />
              </div>

              <div className="relative col-span-3 ml-auto w-[140px]">
                <DeferredNumberInput
                  aria-label={`${label} upper threshold`}
                  value={threshold}
                  min={minimum}
                  max={maximum}
                  disabled={inputDisabled}
                  onCommit={(value) => onThresholdChange(status, value)}
                  className={`${profanityFieldClassName} w-full pr-[36px]`}
                />
                <span className="pointer-events-none absolute right-md top-1/2 -translate-y-1/2 text-text-md text-gray-400">
                  %
                </span>
              </div>
            </div>
          </HelpTooltip>
        )
      })}
    </div>
  )
}

export default ProfanityRuleRows
